import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import validator from 'validator/es';
import orderBy from 'lodash.orderby';
import queryString from 'querystring';
import apiClient from '../../utils/apiClient';
import useForm from '../../hooks/useForm';
import Button from '../Button/Button';
import { withEventTracking } from '../../utils/tracking';

const calculatePrice = (tickets) => {
  const silverCount = tickets.filter(({ type }) => type === 'silver').length;
  const goldCount = tickets.filter(({ type }) => type === 'gold').length;
  const platinumCount = tickets.filter(({ type }) => type === 'platinum')
    .length;

  return silverCount * 99 + goldCount * 999 + platinumCount * 9999;
};

const steps = {
  SELECT_TICKET: 1,
  ENTER_DETAILS: 2,
  CHECKOUT: 3,
  BOOKING_SUCCESS: 4,
  BOOKING_FAILURE: 5,
  EDIT_DETAILS: 6,
  ENTER_COUPON: 7,
  COUPON_CONFIRM: 8,
};

const BookingModal = ({
  bookingModalOpen,
  bookingModalOpenToggle,
  location,
  offer99 = false,
  mentorship = false,
}) => {
  const [bookingModalStep, setBookingModalStep] = useState(1);
  const [editIndex, setEditIndex] = useState(10);

  const [loading, setLoading] = useState(false);

  const [invalidCouponCode, setInvalidCouponCode] = useState(false);

  const formConfig = {
    initialValues: {
      tickets: [],
      couponCode: '',
    },
    validations: ({ tickets }) => ({
      ...[...new Array(tickets.length).keys()].reduce(
        (a, c) => ({
          ...a,
          [`tickets[${c}].name`]: {
            required: bookingModalStep === (editIndex !== 10 ? 6 : 2),
            message: 'Name is required.',
          },
          [`tickets[${c}].email`]: {
            required: bookingModalStep === (editIndex !== 10 ? 6 : 2),
            message: 'Email is required.',
            rules: [
              {
                validator: (v) =>
                  bookingModalStep === (editIndex !== 10 ? 6 : 2)
                    ? validator.isEmail(v)
                    : true,
                message: 'This is an invalid email.',
              },
            ],
          },
          [`tickets[${c}].phoneNumber`]: {
            required: bookingModalStep === (editIndex !== 10 ? 6 : 2),
            message: 'Contact number is required.',
            rules: [
              {
                validator: (v) =>
                  bookingModalStep === (editIndex !== 10 ? 6 : 2)
                    ? validator.isMobilePhone(v)
                    : true,
                message: 'This is an invalid contact number.',
              },
            ],
          },
        }),
        {},
      ),
    }),
    onSubmit: ({ couponCode }) => {
      switch (bookingModalStep) {
        case steps.ENTER_DETAILS: {
          withEventTracking('ENTER_DETAILS')();
          setBookingModalStep(steps.CHECKOUT);
          break;
        }

        case steps.ENTER_COUPON: {
          (async () => {
            setLoading(true);

            try {
              const {
                data: { valid },
              } = await apiClient.post('/verifyCouponCode', {
                couponCode,
              });

              if (valid) {
                setBookingModalStep(steps.COUPON_CONFIRM);
              } else {
                setInvalidCouponCode(true);
              }
            } catch (e) {
              if (e?.response?.data) {
                alert(e?.response?.data?.message);
              } else {
                alert('Something went wrong, try again!');
              }
            } finally {
              setLoading(false);
            }
          })();

          break;
        }

        case steps.CHECKOUT: {
          if (mentorship) {
            setBookingModalStep(steps.ENTER_COUPON);
            break;
          }
        }

        // eslint-disable-next-line no-fallthrough
        case steps.COUPON_CONFIRM: {
          (async () => {
            try {
              await checkout();
            } catch (e) {
              /* do nothing */
            }
          })();
          break;
        }

        case steps.BOOKING_FAILURE: {
          setBookingModalStep(steps.CHECKOUT);
          setEditIndex(10);
          break;
        }

        case steps.EDIT_DETAILS: {
          withEventTracking('REVIEW_DETAILS')();
          setBookingModalStep(steps.CHECKOUT);
          setEditIndex(10);
          break;
        }

        default: {
          setBookingModalStep((state) => state + 1);
        }
      }
    },
  };

  const { values, onChange, onSubmit, getErrors, reset } = useForm(formConfig);

  const { tickets, couponCode } = values;

  const onReviewTicket = useCallback(
    (index) => () => {
      setEditIndex(index);
    },
    [],
  );

  const incrementCount = useCallback(
    (type) => () => {
      onChange(`tickets[${tickets.length}]`, {
        name: '',
        email: '',
        phoneNumber: '',
        type,
      });
    },
    [onChange],
  );

  const decrementCount = useCallback(
    (t) => () => {
      onChange('tickets', [
        ...tickets.filter(({ type }) => type !== t),
        ...tickets
          .filter(({ type }) => type === t)
          .splice(0, tickets.filter(({ type }) => type === t).length - 1),
      ]);
    },
    [tickets, onChange],
  );

  const getTicketsCount = useCallback(() => {
    const silverCount = tickets.filter(({ type }) => type === 'silver').length;
    const goldCount = tickets.filter(({ type }) => type === 'gold').length;
    const platinumCount = tickets.filter(({ type }) => type === 'platinum')
      .length;

    const message = [];
    if (silverCount) {
      message.push(`${silverCount} Silver`);
    }
    if (goldCount) {
      message.push(`${goldCount} Gold`);
    }
    if (platinumCount) {
      message.push(`${platinumCount} Platinum`);
    }

    return message.join(', ');
  }, [tickets]);

  const getTotalPrice = useCallback(() => calculatePrice(tickets), [tickets]);

  const handleChange = useCallback(
    ({ target: { name, value } }) => {
      if (name === 'couponCode' && invalidCouponCode) {
        setInvalidCouponCode(false);
      }

      onChange(name, value);
    },
    [onChange, invalidCouponCode],
  );

  const checkout = useCallback(async () => {
    setLoading(true);

    try {
      const amount = calculatePrice(tickets) * 100;

      const {
        data: { id },
      } = await apiClient.post('/createOrder', {
        amount,
      });

      const options = {
        key:
          process.env.RZP_KEY_ID || process.env.NODE_ENV === 'development'
            ? 'rzp_test_Vdq3ECEJJecYk8'
            : 'rzp_live_a8n4XjViE3IcTe',
        amount,
        currency: 'INR',
        name: "FTX'21",
        description: 'The Ultimate FinTech Conference for Disruptors',
        image: 'https://ftx-rp.netlify.app/favicon-32x32.png',
        order_id: id,
        async handler(response) {
          setLoading(true);

          const params = queryString.parse(location.search.slice(1));

          try {
            /* eslint-disable camelcase */
            const {
              razorpay_payment_id,
              razorpay_order_id,
              razorpay_signature,
            } = response;

            const {
              data: { checksum },
            } = await apiClient.post('/generateChecksum', {
              orderID: razorpay_order_id,
              rzpPaymentID: razorpay_payment_id,
            });

            if (razorpay_signature === checksum) {
              await apiClient.post('/bookingsCreate', {
                payload: tickets.map((t) => ({
                  ...t,
                  ...(params.coupon_code
                    ? { couponCode: params.coupon_code }
                    : {}),
                  ...(params.utm_source
                    ? { utmSource: params.utm_source }
                    : {}),
                  ...(params.utm_medium
                    ? { utmMedium: params.utm_medium }
                    : {}),
                  ...(params.utm_campaign
                    ? { utmCampaign: params.utm_campaign }
                    : {}),
                  ...(params.utm_campaign_id
                    ? { utmCampaignID: params.utm_campaign_id }
                    : {}),
                })),
              });

              setBookingModalStep(steps.BOOKING_SUCCESS);
              withEventTracking('SUCCESS')();
            } else {
              alert('Unable to complete your payment.');

              setBookingModalStep(steps.BOOKING_FAILURE);
            }
            /* eslint-enable camelcase */
          } catch (e) {
            if (e?.response?.data) {
              alert(e?.response?.data?.message);
            } else {
              alert('Something went wrong, try again!');
            }
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: tickets[0].name,
          email: tickets[0].email,
          contact: tickets[0].phoneNumber,
        },
        theme: {
          color: '#663399',
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on('payment.failed', function () {
        setLoading(false);
        // setBookingModalStep(5);
      });

      rzp.open();
    } catch (e) {
      if (e?.response?.data) {
        alert(e?.response?.data?.message);
      } else {
        alert('Something went wrong, try again!');
      }
    } finally {
      setLoading(false);
    }
  }, [tickets, location]);

  useEffect(() => {
    if (editIndex !== 10) {
      setBookingModalStep(6);
    }
  }, [editIndex]);

  useEffect(() => {
    if (!bookingModalOpen) {
      reset();
      setBookingModalStep(1);
      setEditIndex(10);
    }
  }, [bookingModalOpen]);

  return (
    <div
      className={`booking-modal-main ${bookingModalOpen ? 'modal-open' : ''}`}>
      <div className="booking-modal-wrapper">
        <div className="booking-modal-block">
          <button
            type="button"
            onClick={bookingModalOpenToggle}
            className="close-video-modal">
            <span className="close-popup">&nbsp;</span>
          </button>
          <div className="booking-modal-header">
            {/* <LogoAnim /> */}
            <img
              src="https://ftx-rp.netlify.app/img/ftx21.png"
              className="logo"
              loading="lazy"
              alt=""
            />
            <p>9th - 10th December ‘21</p>
          </div>

          <form onSubmit={onSubmit} autoComplete="off">
            {/* STEP 1 */}
            <div
              className={`booking-step booking-step-1 ${
                bookingModalStep === steps.SELECT_TICKET ? 'active' : ''
              }`}>
              <h3 className="modal-head center" style={{ marginBottom: 20 }}>
                Select your paid tickets
              </h3>
              <div className="booking-modal-body">
                <div className="row align-normal">
                  <div className="col-md col-4">
                    <div className="single-modal-price">
                      <div className="flex align-center justify-space-between">
                        <div className="modal-price">
                          <span className="price-category category-small">
                            SILVER
                          </span>
                          <p className="price">₹99</p>
                        </div>

                        <div className="ticket-count-selector">
                          <div className="participants-couter">
                            <button
                              className="minus-btn"
                              type="button"
                              onClick={withEventTracking(
                                'DECREMENT',
                                decrementCount('silver'),
                              )}
                              disabled={tickets.length === 0}>
                              -
                            </button>
                            <input
                              min="1"
                              value={
                                tickets.filter(({ type }) => type === 'silver')
                                  .length
                              }
                              readOnly
                            />
                            <button
                              type="button"
                              disabled={tickets.length === 10}
                              onClick={withEventTracking(
                                'INCREMENT',
                                incrementCount('silver'),
                              )}>
                              +
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="modal-price-content">
                        <p>We save you a seat at FTX 2021 😃</p>

                        <p>
                          You also help children without internet access get
                          learning material and worksheets.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md col-4">
                    <div
                      className={`single-modal-price gold-price ${
                        offer99 ? 'disabled' : ''
                      }`}>
                      <div className="flex align-center justify-space-between">
                        <div className="modal-price">
                          <span className="price-category category-small">
                            GOLD
                          </span>
                          <p className="price">₹999</p>
                        </div>

                        <div className="ticket-count-selector">
                          <div className="participants-couter">
                            <button
                              onClick={withEventTracking(
                                'DECREMENT',
                                decrementCount('gold'),
                              )}
                              className="minus-btn"
                              type="button"
                              disabled={offer99 || tickets.length === 0}>
                              -
                            </button>
                            <input
                              min="1"
                              value={
                                tickets.filter(({ type }) => type === 'gold')
                                  .length
                              }
                              readOnly
                            />
                            <button
                              type="button"
                              disabled={tickets.length === 10 || offer99}
                              onClick={withEventTracking(
                                'INCREMENT',
                                incrementCount('gold'),
                              )}>
                              +
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="modal-price-content">
                        <p>
                          Get an exclusive goodie bag along with your seat at
                          FTX 2021 😋
                        </p>

                        <p>
                          You also help children get access to mental health and
                          personal development support.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md col-4">
                    <div
                      className={`single-modal-price platinum-price ${
                        offer99 ? 'disabled' : ''
                      }`}>
                      <div className="flex align-center justify-space-between">
                        <div className="modal-price">
                          <span className="price-category category-small">
                            PLATINUM
                          </span>
                          <p className="price">₹9999</p>
                        </div>

                        <div className="ticket-count-selector">
                          <div className="participants-couter">
                            <button
                              onClick={withEventTracking(
                                'DECREMENT',
                                decrementCount('platinum'),
                              )}
                              className="minus-btn"
                              type="button"
                              disabled={offer99 || tickets.length === 0}>
                              -
                            </button>
                            <input
                              min="1"
                              value={
                                tickets.filter(
                                  ({ type }) => type === 'platinum',
                                ).length
                              }
                              readOnly
                            />
                            <button
                              type="button"
                              disabled={tickets.length === 10 || offer99}
                              onClick={withEventTracking(
                                'INCREMENT',
                                incrementCount('platinum'),
                              )}>
                              +
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="modal-price-content">
                        <p>
                          Stand a chance to meet Razorpay leaders &amp; get an
                          exclusive goodie bag along with your seat at FTX 2021
                          ☺️
                        </p>

                        <p>
                          You also help fund bridge schools for children facing
                          learning gaps due to the pandemic.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-center align-center booking-modal-footer">
                <div className="total-price-with-tickets">
                  <h4>₹{getTotalPrice()}</h4>
                  <p>{getTicketsCount()}</p>
                </div>
                <Button type="submit" primary disabled={tickets.length === 0}>
                  BOOK YOUR TICKET
                </Button>
              </div>
            </div>

            {/* STEP 2 */}
            <div
              className={`booking-step booking-step-2 ${
                bookingModalStep === steps.ENTER_DETAILS ? 'active' : ''
              }`}>
              <div className="booking-modal-body">
                <div className="row justify-flex-start">
                  {orderBy(
                    tickets.map(({ type, ...r }) => ({
                      ...r,
                      type,
                      sortOrder: { silver: 1, gold: 2, platinum: 3 }[type],
                    })),
                    'sortOrder',
                  ).map(({ name, email, phoneNumber, type }, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div className="col col-6" key={i}>
                      <h3 className="form-head">
                        Attendee {i + 1}{' '}
                        <span
                          className={`price-category category-small ${
                            type === 'gold' ? 'gold' : ''
                          } ${type === 'platinum' ? 'platinum' : ''}`}>
                          {type.toUpperCase()}
                        </span>
                      </h3>
                      <div className="form-group">
                        <input
                          type="text"
                          className={`form-control ${
                            getErrors(`tickets[${i}].name`).length
                              ? 'form-error'
                              : ''
                          }`}
                          placeholder="Name"
                          name={`tickets[${i}].name`}
                          value={name}
                          onChange={handleChange}
                          autoComplete="off"
                        />
                        <div className="row">
                          <div className="col col-6">
                            <input
                              type="text"
                              className={`form-control ${
                                getErrors(`tickets[${i}].email`).length
                                  ? 'form-error'
                                  : ''
                              }`}
                              placeholder="Email"
                              name={`tickets[${i}].email`}
                              value={email}
                              onChange={handleChange}
                              autoComplete="off"
                            />
                          </div>
                          <div className="col col-6">
                            <input
                              type="text"
                              className={`form-control ${
                                getErrors(`tickets[${i}].phoneNumber`).length
                                  ? 'form-error'
                                  : ''
                              }`}
                              placeholder="Phone"
                              name={`tickets[${i}].phoneNumber`}
                              value={phoneNumber}
                              onChange={handleChange}
                              autoComplete="off"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="center booking-modal-footer">
                <Button type="submit" primary>
                  PROCEED
                </Button>
              </div>
            </div>

            {/* COUPON CODE STEP */}
            <div
              className={`booking-step booking-step-coupon ${
                bookingModalStep === steps.ENTER_COUPON ? 'active' : ''
              }`}>
              <h3 className="modal-head center" style={{ marginBottom: 30 }}>
                Do you have a Promo Code?
              </h3>
              <div className="booking-modal-body">
                <div className="promocode-input">
                  <input
                    type="text"
                    className={`form-control ${
                      invalidCouponCode ? 'form-error' : ''
                    }`}
                    name="couponCode"
                    value={couponCode}
                    placeholder="Enter Promo Code"
                    onChange={handleChange}
                    autoComplete="off"
                  />
                  {invalidCouponCode && (
                    <p className="coupon-error">Coupon code not valid</p>
                  )}
                  {/* <p className="gradient-text coupon-correct"> */}
                  {/*  You will be considered for the Mentorship Session shortlist */}
                  {/* </p> */}
                </div>
              </div>
              <div className="center booking-modal-footer">
                <Button
                  type="submit"
                  primary
                  disabled={loading || couponCode === ''}>
                  APPLY NOW
                </Button>
                <button
                  className="button-unstyled skip-button "
                  type="button"
                  onClick={checkout}
                  disabled={loading}>
                  SKIP
                </button>
              </div>
            </div>

            {/* COUPON STEP SUCCESS */}
            <div
              className={`booking-step booking-step-coupon ${
                bookingModalStep === steps.COUPON_CONFIRM ? 'active' : ''
              }`}>
              <div className="success-message final-message">
                <img src="/img/gift-box.svg" loading="lazy" alt="" />
                <h2>Coupon Applied!</h2>
                <p style={{ maxWidth: 600, margin: '0 auto' }}>
                  You will be now be considered for a one on one mentorship
                  session with the{' '}
                  <span className="bold">Razorpay leadership team</span>. We'll
                  announce the winners shortly.
                </p>
                <div className="booking-modal-footer">
                  <Button type="submit" primary disabled={loading}>
                    PROCEED TO CHECKOUT
                  </Button>
                </div>
              </div>
            </div>

            {/* STEP 3 */}
            <div
              className={`booking-step booking-step-3 ${
                bookingModalStep === steps.CHECKOUT ? 'active' : ''
              }`}>
              <h3 className="modal-head center" style={{ marginBottom: 30 }}>
                Review your ticket details
              </h3>
              <div className="booking-modal-body">
                <div className="row justify-flex-start">
                  {tickets.map(({ name, email, phoneNumber, type }, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={i} className="col col-3">
                      <div
                        className={`review-ticket-single ${
                          type === 'gold' ? 'gold-price' : ''
                        } ${type === 'platinum' ? 'platinum-price' : ''}`}>
                        <div className="review-head flex justify-space-between">
                          <p>ATTENDEE {i + 1}</p>
                          <button
                            onClick={onReviewTicket(i)}
                            className="button-unstyled"
                            type="button">
                            <img
                              src="https://ftx-rp.netlify.app/img/edit.svg"
                              alt=""
                            />
                          </button>
                        </div>
                        <div className="review-details">
                          <p className="bold">{name}</p>
                          <p>{email}</p>
                          <p>{phoneNumber}</p>
                          <span className="price-category category-small">
                            {type.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="center booking-modal-footer">
                <Button type="submit" primary disabled={loading}>
                  PROCEED
                </Button>
              </div>
            </div>

            {/* STEP 4 */}
            <div
              className={`booking-step booking-step-4 ${
                bookingModalStep === steps.BOOKING_SUCCESS ? 'active' : ''
              }`}>
              <div className="success-message final-message">
                <img
                  src="https://ftx-rp.netlify.app/img/success.svg"
                  loading="lazy"
                  alt=""
                />
                <h2>You’re all set!</h2>
                <p>
                  Your tickets will be sent to your registered email IDs. Do
                  reach out to us at{' '}
                  <a href="mailto:ftx@razorpay.com" className="bold">
                    ftx@razorpay.com
                  </a>{' '}
                  for any further queries.
                </p>
                <div className="booking-modal-footer">
                  <Button primary onClick={bookingModalOpenToggle}>
                    BACK TO FTX
                  </Button>
                </div>
              </div>
            </div>

            {/* STEP 5 */}

            <div
              className={`booking-step booking-step-4 ${
                bookingModalStep === steps.BOOKING_FAILURE ? 'active' : ''
              }`}>
              <div className="fail-message final-message">
                <img
                  src="https://ftx-rp.netlify.app/img/fail.svg"
                  loading="lazy"
                  alt=""
                />
                <h2>Oh no!</h2>
                <p>
                  Seems like there is some error with your interent connectivity
                </p>
                <div className="booking-modal-footer">
                  <Button type="submit" primary>
                    RETRY NOW
                  </Button>
                </div>
              </div>
            </div>

            {/* Edit Details */}
            <div
              className={`booking-step booking-step-2 ${
                bookingModalStep === steps.EDIT_DETAILS ? 'active' : ''
              }`}>
              <h3
                className="form-head justify-center"
                style={{ marginBottom: 40 }}>
                Edit your ticket details
              </h3>
              <div className="booking-modal-body">
                <div className="row">
                  {tickets
                    .filter((_, i) => i === editIndex)
                    .map(({ name, email, phoneNumber, type }) => (
                      <div className="col col-6">
                        <h3 className="form-head">
                          Attendee {editIndex + 1}{' '}
                          <span
                            className={`price-category category-small ${
                              type === 'gold' ? 'gold' : ''
                            } ${type === 'platinum' ? 'platinum' : ''}`}>
                            {type.toUpperCase()}
                          </span>
                        </h3>
                        <div className="form-group">
                          <input
                            type="text"
                            className={`form-control ${
                              getErrors(`tickets[${editIndex}].name`).length
                                ? 'form-error'
                                : ''
                            }`}
                            placeholder="Name"
                            name={`tickets[${editIndex}].name`}
                            value={name}
                            onChange={handleChange}
                            autoComplete="off"
                          />
                          <div className="row">
                            <div className="col col-6">
                              <input
                                type="text"
                                className={`form-control ${
                                  getErrors(`tickets[${editIndex}].email`)
                                    .length
                                    ? 'form-error'
                                    : ''
                                }`}
                                placeholder="Email"
                                name={`tickets[${editIndex}].email`}
                                value={email}
                                onChange={handleChange}
                                autoComplete="off"
                              />
                            </div>
                            <div className="col col-6">
                              <input
                                type="text"
                                className={`form-control ${
                                  getErrors(`tickets[${editIndex}].phoneNumber`)
                                    .length
                                    ? 'form-error'
                                    : ''
                                }`}
                                placeholder="Phone"
                                name={`tickets[${editIndex}].phoneNumber`}
                                value={phoneNumber}
                                onChange={handleChange}
                                autoComplete="off"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
              <div className="center booking-modal-footer">
                <Button type="submit" primary>
                  CONFIRM
                </Button>
              </div>
            </div>
          </form>
        </div>

        <div className="conf-by-razorpay hidden-mobile">
          <p>
            A virtual conference by{' '}
            <img
              src="https://ftx-rp.netlify.app/img/razorpay-logo.svg"
              loading="lazy"
              alt="razorpay"
            />
          </p>
        </div>
      </div>
    </div>
  );
};

BookingModal.propTypes = {
  bookingModalOpen: PropTypes.bool.isRequired,
  bookingModalOpenToggle: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  offer99: PropTypes.bool.isRequired,
  mentorship: PropTypes.bool.isRequired,
};

export default BookingModal;
